import { useState, useEffect } from 'react'
import { addAllowedEmail, removeAllowedEmail, fetchAllowedOwners, createUnclaimedGallery, fetchUnclaimedGalleries, deleteGallery } from '../lib/api'
import QrEditorModal, { GALLERY_BASE_URL } from '../components/QrEditorModal'
import LoadingDots from '../components/LoadingDots'

interface AllowForm {
  email: string
  isAdmin: boolean
}

const emptyAllowForm: AllowForm = { email: '', isAdmin: false }

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none">
      <div className="relative">
        <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <div className={`w-10 h-6 rounded-full transition-colors duration-200 ${checked ? 'bg-blue-500' : 'bg-gray-700'}`} />
        <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
      </div>
      <span className="text-sm text-gray-400">Administrator</span>
    </label>
  )
}

function Spinner() {
  return <LoadingDots />
}

export default function Admin() {
  const [allowForm, setAllowForm] = useState<AllowForm>(emptyAllowForm)
  const [allowSubmitting, setAllowSubmitting] = useState(false)
  const [allowSuccess, setAllowSuccess] = useState<string | null>(null)
  const [allowError, setAllowError] = useState<string | null>(null)

  const [allowedOwners, setAllowedOwners] = useState<{ email: string; isAdmin: boolean }[]>([])
  const [allowedLoading, setAllowedLoading] = useState(true)
  const [removingEmail, setRemovingEmail] = useState<string | null>(null)

  const [shareCreating, setShareCreating] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [shareError, setShareError] = useState<string | null>(null)

  const [unclaimed, setUnclaimed] = useState<{ id: string; shortCode: string; createdAt: string; claimDescription: string | null }[]>([])
  const [unclaimedLoading, setUnclaimedLoading] = useState(true)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [shareDescription, setShareDescription] = useState('')
  const [qrGallery, setQrGallery] = useState<{ shortCode: string; name: string | null } | null>(null)

  useEffect(() => {
    fetchUnclaimedGalleries()
      .then(setUnclaimed)
      .finally(() => setUnclaimedLoading(false))
    fetchAllowedOwners()
      .then(setAllowedOwners)
      .finally(() => setAllowedLoading(false))
  }, [])

  const handleCreateShareLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setShareCreating(true)
    setShareError(null)
    setShareUrl(null)
    try {
      const { id, shortCode } = await createUnclaimedGallery(shareDescription || undefined)
      const url = `${window.location.origin}/${shortCode}`
      setShareUrl(url)
      setUnclaimed((prev) => [{ id, shortCode, createdAt: new Date().toISOString(), claimDescription: shareDescription.trim() || null }, ...prev])
      setShareDescription('')
    } catch (err) {
      setShareError((err as Error).message)
    } finally {
      setShareCreating(false)
    }
  }

  const handleDeleteUnclaimed = async (id: string) => {
    if (!window.confirm('Slett dette galleriet? Dette kan ikke angres.')) return
    setDeletingId(id)
    try {
      await deleteGallery(id)
      setUnclaimed((prev) => prev.filter((g) => g.id !== id))
    } finally {
      setDeletingId(null)
    }
  }

  const handleCopy = (id: string, shortCode: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/${shortCode}`)
    setCopiedId(id)
    setTimeout(() => setCopiedId((prev) => (prev === id ? null : prev)), 2000)
  }

  const handleAllow = async (e: React.FormEvent) => {
    e.preventDefault()
    setAllowSubmitting(true)
    setAllowError(null)
    setAllowSuccess(null)
    try {
      await addAllowedEmail(allowForm.email, allowForm.isAdmin)
      setAllowSuccess(`${allowForm.email} lagt til`)
      // Optimistically add to list (upsert: replace if already present)
      setAllowedOwners((prev) => {
        const filtered = prev.filter((o) => o.email !== allowForm.email)
        return [...filtered, { email: allowForm.email, isAdmin: allowForm.isAdmin }]
          .sort((a, b) => a.email.localeCompare(b.email))
      })
      setAllowForm(emptyAllowForm)
    } catch (err) {
      setAllowError((err as Error).message)
    } finally {
      setAllowSubmitting(false)
    }
  }

  const handleRemoveAllowed = async (email: string) => {
    if (!window.confirm(`Fjern ${email} fra tillatelselisten?`)) return
    setRemovingEmail(email)
    try {
      await removeAllowedEmail(email)
      setAllowedOwners((prev) => prev.filter((o) => o.email !== email))
    } finally {
      setRemovingEmail(null)
    }
  }

  return (
    <>
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* Share links — create form + pending list in one card */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4 text-gray-200">Galleri for deling</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">

            {/* Create form */}
            <form onSubmit={handleCreateShareLink} className="p-6 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="share-description" className="text-sm text-gray-400">
                  Beregnet for <span className="text-gray-600">(valgfritt)</span>
                </label>
                <input
                  id="share-description"
                  type="text"
                  value={shareDescription}
                  onChange={(e) => setShareDescription(e.target.value)}
                  placeholder="f.eks. Ingrid Johnsen"
                  className="
                    bg-gray-800 border border-gray-700 rounded-lg
                    px-3 py-2 text-base text-white
                    focus:outline-none focus:border-blue-500
                    placeholder-gray-600
                  "
                />
              </div>
              {shareError && (
                <div className="rounded-lg bg-red-900 border border-red-700 px-4 py-3 text-red-200 text-sm">{shareError}</div>
              )}
              {shareUrl && (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-gray-400">Del denne lenken med brukeren:</p>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={shareUrl}
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-base text-white font-mono outline-none"
                      onFocus={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      onClick={() => navigator.clipboard.writeText(shareUrl)}
                      className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors whitespace-nowrap"
                    >
                      Kopier
                    </button>
                    <button
                      type="button"
                      onClick={() => setShareUrl(null)}
                      className="p-2 text-gray-500 hover:text-white transition-colors rounded-lg"
                      aria-label="Lukk"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              <button
                type="submit"
                disabled={shareCreating}
                className="
                  flex items-center justify-center gap-2
                  px-4 py-2
                  bg-gray-700 hover:bg-gray-600
                  disabled:bg-gray-800 disabled:cursor-not-allowed
                  text-gray-200 text-sm font-medium
                  rounded-lg transition-colors duration-200
                "
              >
                {shareCreating ? <Spinner /> : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                  </svg>
                )}
                Opprett galleri
              </button>
            </form>

            {/* Pending list */}
            <div className="border-t border-gray-800">
              {unclaimedLoading ? (
                <div className="divide-y divide-gray-800">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="px-4 py-3 flex items-center gap-3">
                      <div className="flex-1 flex flex-col gap-1.5">
                        <div className="h-4 w-40 rounded bg-gray-700 animate-pulse" />
                        <div className="h-3 w-64 rounded bg-gray-700 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : unclaimed.length === 0 ? (
                <p className="px-4 py-5 text-sm text-gray-600 text-center">Ingen galleri.</p>
              ) : (
                <ul className="divide-y divide-gray-800">
                  {unclaimed.map((g) => {
                    const url = `${window.location.origin}/${g.shortCode}`
                    const date = new Date(g.createdAt).toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric' })
                    return (
                      <li key={g.id} className="flex items-center gap-3 px-4 py-3">
                        <div className="flex-1 flex flex-col gap-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-white font-medium truncate">
                              {g.claimDescription ?? <span className="text-gray-500 italic font-normal">Ingen beskrivelse</span>}
                            </span>
                            <span className="text-xs text-gray-600 whitespace-nowrap flex-shrink-0">{date}</span>
                          </div>
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="font-mono text-xs text-gray-500 hover:text-blue-400 truncate"
                          >
                            {url}
                          </a>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCopy(g.id, g.shortCode)}
                          className="px-2.5 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-200 text-xs rounded-lg transition-colors whitespace-nowrap"
                        >
                          {copiedId === g.id ? 'Kopiert!' : 'Kopier'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setQrGallery({ shortCode: g.shortCode, name: g.claimDescription })}
                          className="p-1.5 text-gray-600 hover:text-white transition-colors rounded"
                          aria-label="QR-kode"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteUnclaimed(g.id)}
                          disabled={deletingId === g.id}
                          className="p-1.5 text-gray-600 hover:text-red-400 disabled:opacity-40 transition-colors rounded"
                          aria-label="Slett"
                        >
                          {deletingId === g.id ? (
                            <Spinner />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                          )}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

          </div>
        </section>

        {/* Allow login section */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4 text-gray-200">Tillat innlogging</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">

            {/* Add form */}
            <form onSubmit={handleAllow} className="p-6 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="allow-email" className="text-sm text-gray-400">E-post</label>
                <input
                  id="allow-email"
                  type="email"
                  required
                  value={allowForm.email}
                  onChange={(e) => setAllowForm((f) => ({ ...f, email: e.target.value }))}
                  className="
                    bg-gray-800 border border-gray-700 rounded-lg
                    px-3 py-2 text-base text-white
                    focus:outline-none focus:border-blue-500
                    placeholder-gray-600
                  "
                  placeholder="bruker@example.com"
                />
              </div>
              <Toggle checked={allowForm.isAdmin} onChange={(v) => setAllowForm((f) => ({ ...f, isAdmin: v }))} />
              {allowError && (
                <div className="rounded-lg bg-red-900 border border-red-700 px-4 py-3 text-red-200 text-sm">{allowError}</div>
              )}
              {allowSuccess && (
                <div className="rounded-lg bg-green-900 border border-green-700 px-4 py-3 text-green-200 text-sm">{allowSuccess}</div>
              )}
              <button
                type="submit"
                disabled={allowSubmitting}
                className="
                  flex items-center justify-center gap-2
                  px-4 py-2 mt-1
                  bg-blue-500 hover:bg-blue-600
                  disabled:bg-gray-700 disabled:cursor-not-allowed
                  text-white text-sm font-medium
                  rounded-lg transition-colors duration-200
                "
              >
                {allowSubmitting ? <Spinner /> : null}
                Legg til
              </button>
            </form>

            {/* Allowed owners list */}
            <div className="border-t border-gray-800">
              {allowedLoading ? (
                <div className="divide-y divide-gray-800">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="px-4 py-3 flex items-center gap-3">
                      <div className="flex-1 h-4 w-48 rounded bg-gray-700 animate-pulse" />
                      <div className="h-4 w-14 rounded bg-gray-700 animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : allowedOwners.length === 0 ? (
                <p className="px-4 py-5 text-sm text-gray-600 text-center">Ingen tillatte e-poster.</p>
              ) : (
                <ul className="divide-y divide-gray-800">
                  {allowedOwners.map((o) => (
                    <li key={o.email} className="flex items-center gap-3 px-4 py-3">
                      <span className="flex-1 text-sm text-white truncate">{o.email}</span>
                      {o.isAdmin && (
                        <span className="text-xs text-blue-400 bg-blue-900/40 border border-blue-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                          Admin
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveAllowed(o.email)}
                        disabled={removingEmail === o.email}
                        className="p-1.5 text-gray-600 hover:text-red-400 disabled:opacity-40 transition-colors rounded"
                        aria-label="Fjern"
                      >
                        {removingEmail === o.email ? (
                          <Spinner />
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

          </div>
        </section>


      </div>
    </div>

    {qrGallery && (
      <QrEditorModal
        galleryUrl={`${GALLERY_BASE_URL}/${qrGallery.shortCode}`}
        galleryName={qrGallery.name}
        onClose={() => setQrGallery(null)}
      />
    )}
    </>
  )
}
